// The Business Dashboard's PREVIOUS per-query path (page speed phase 1, 2026-10-25).
// useAdminDashboardData reads everything from get_admin_dashboard_summary
// (supabase/migrations/20261025_admin_dashboard_summary.sql) and uses this file only when that
// call fails: before the migration is applied (PGRST202), or on any other error, where the old
// page degraded per query instead of blanking every tile. Keeping or retiring it once the
// function is live is an operator decision (it changes what a failed load shows).
// This is the previous hook, unchanged apart from dropping its React Query wrappers.
import { getSupabaseClient } from '../utils/supabaseClient';
import { getDisplayName } from '../utils/formatters';
import { WORK_ORDER_STATUS, INVOICE_STATUS, FINISHED_JOB_STATUSES, ACTIVE_JOB_STATUSES, isAcceptedQuote } from '../constants/statusEnums';
import { fmtDate, companyDateKey, companyWallClockToUtc, addDaysToDateKey } from '../utils/dateFormatters';
import { isReceivable, invoiceBalance } from '../utils/invoiceAr';
import { currentTerm } from '../utils/brandingUtils';
import { getSchedulingSettings, DAY_NAMES } from '../utils/smartScheduling';
import { jobFinishedAt, PROFITABILITY_FINISHED_STATUSES } from './useReportsPageData';

// Helper functions (kept from original)
const calcDelta = (current, previous) => {
  if (!previous || previous === 0 || current === undefined || current === null) return null;
  return Math.round(((current - previous) / previous) * 100);
};

const getPrevPeriod = (preset, start, end) => {
  const s = new Date(start);
  const e = new Date(end);
  
  switch (preset) {
    case 'TODAY':
      s.setDate(s.getDate() - 1);
      e.setDate(e.getDate() - 1);
      break;
    case 'WEEK':
      s.setDate(s.getDate() - 7);
      e.setDate(e.getDate() - 7);
      break;
    case 'MONTH':
      s.setMonth(s.getMonth() - 1);
      e.setMonth(e.getMonth() - 1);
      break;
    default:
      s.setMonth(s.getMonth() - 1);
      e.setMonth(e.getMonth() - 1);
  }
  
  return { start: s, end: e };
};

const countWeekdays = (s, e) => {
  let d = new Date(s), cnt = 0;
  d.setHours(0,0,0,0);
  const endD = new Date(e); endD.setHours(0,0,0,0);
  while (d <= endD) {
    const day = d.getDay();
    if (day !== 0 && day !== 6) cnt++;
    d.setDate(d.getDate() + 1);
  }
  return cnt;
};

// Accounts-receivable invoice statuses. Drafts aren't issued yet, and
// paid/void/cancelled/written_off aren't collectable — none of them are money
// owed, so none count toward "outstanding"/"overdue" A/R. A/R = sent, viewed,
// partially_paid, overdue, disputed. Used by every A/R figure on the dashboard
// so the numbers agree (previously some cards used status != 'paid', which
// wrongly swept in drafts and voids).
const AR_INVOICE_STATUSES = [
  INVOICE_STATUS.SENT,
  INVOICE_STATUS.VIEWED,
  INVOICE_STATUS.PARTIALLY_PAID,
  INVOICE_STATUS.OVERDUE,
  INVOICE_STATUS.DISPUTED
];
const isReceivableInvoice = (status) => AR_INVOICE_STATUSES.includes(status);

// ============== FETCH FUNCTIONS ==============

// 1) Active Employees
const fetchActiveEmployees = async (companyId) => {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('employees')
    .select('id', { count: 'exact' })
    .eq('company_id', companyId)
    .eq('status', 'active');
  return error ? 0 : (data?.length || 0);
};

// 2) Active Jobs — a CURRENT-STATE count, deliberately outside the reporting range. It
// answers "what is on the go right now", which no choice of period changes.
//
// Board #974: the status set is the shared ACTIVE_JOB_STATUSES rather than a local pair.
// This card and the Reports card of the same name disagreed (52 vs 13 on one company on one
// day) partly because each wrote out its own definition.
const fetchActiveJobs = async (companyId) => {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('work_orders')
    .select('id', { count: 'exact' })
    .eq('company_id', companyId)
    .in('status', ACTIVE_JOB_STATUSES);
  return error ? 0 : (data?.length || 0);
};

/**
 * 2b) Jobs in the selected range — the population "Jobs This Range" names and the
 * denominator Completion Rate divides by.
 *
 * Both of those used to be fed by fetchActiveJobs above, which takes no dates. So the card
 * headed "Jobs This Range" showed the same number for Today as for Last 30 Days (51 live on
 * 48f32d34 on 2026-09-18, against 25 work orders actually created in the last 30 days), and
 * Completion Rate divided jobs COMPLETED in the range by jobs ACTIVE right now — two
 * disjoint sets, since a completed job is by definition neither scheduled nor in progress.
 * The rate could exceed 100% as easily as sit at 0.
 *
 * The status set INCLUDES completed on purpose: the numerator has to be a subset of the
 * denominator or the ratio means nothing. This mirrors useDashboardData.js:55-62, which had
 * it right all along.
 */
const fetchJobsInRange = async (companyId, startISO, endISO) => {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('work_orders')
    .select('id', { count: 'exact' })
    .eq('company_id', companyId)
    .in('status', [
      WORK_ORDER_STATUS.SCHEDULED,
      WORK_ORDER_STATUS.IN_PROGRESS,
      WORK_ORDER_STATUS.COMPLETED,
      WORK_ORDER_STATUS.CANCELLED,
      WORK_ORDER_STATUS.INVOICED
    ])
    .gte('created_at', startISO)
    .lte('created_at', endISO);
  return error ? 0 : (data?.length || 0);
};

// 3) Cash Revenue
const fetchCashRevenue = async (companyId, startISO, endISO) => {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('payments')
    .select('amount, refund_amount, received_at')
    .eq('company_id', companyId)
    .in('status', ['completed', 'partially_refunded'])
    .gte('received_at', startISO)
    .lte('received_at', endISO);
  
  if (error || !data) return { cashRevenue: 0, revenueSeries: [], paysArr: [] };
  
  const paysArr = Array.isArray(data) ? data : [];
  // Net of refunds (operator ruling 2026-10-17, "industry standard"): a $750 payment with $300
// refunded counted as $750 collected; it is $450 kept. Fully refunded payments are status
// 'refunded' and were already excluded.
  const netOf = (p) => Math.max(0, (Number(p.amount) || 0) - (Number(p.refund_amount) || 0));
  const cashRevenue = paysArr.reduce((sum, p) => sum + netOf(p), 0);
  
  // Build revenue series
  // One bucket per COMPANY calendar day. These were UTC days, so every payment taken after
  // 5pm Pacific landed in tomorrow's bar.
  const days = [];
  const endKey = companyDateKey(endISO);
  for (let k = companyDateKey(startISO); k && endKey && k <= endKey; k = addDaysToDateKey(k, 1)) {
    days.push(k);
  }
  const daily = Object.fromEntries(days.map(d => [d, 0]));
  paysArr.forEach(p => {
    const day = companyDateKey(p.received_at);
    if (daily[day] != null) daily[day] += netOf(p);
  });
  const revenueSeries = days.map(d => daily[d] || 0);
  
  return { cashRevenue, revenueSeries, paysArr };
};

// 4) Work finished or cancelled in the period: ONE read shared by Completion Rate, the
// Leaderboard and the Tech Scorecards (fix list 2026-10-24 #7). Completion Rate and the
// Leaderboard counted status === 'completed' by created_at, so a job left the count the moment
// it was invoiced and a job finished this month counted in the month it was quoted (48f32d34,
// last 30 days live 2026-10-24: 12 against 49 finished). Finished = the Profitability ruling's
// definition (PRODUCT_DECISIONS "Profitability is on a jobs-completed basis"), owned by
// jobFinishedAt in useReportsPageData: status completed/invoiced/paid/closed, finished date =
// completed_at, else actual_end / invoiced_at / paid_at / closed_at. Cancelled = status
// cancelled with cancelled_at in the period.
const FINISHED_DATE_COLUMNS = ['completed_at', 'actual_end', 'invoiced_at', 'paid_at', 'closed_at'];
const fetchFinishedWorkInPeriod = async (companyId, startISO, endISO) => {
  const supabase = getSupabaseClient();
  const inRange = (col) => `and(${col}.gte.${startISO},${col}.lte.${endISO})`;
  const { data, error } = await supabase
    .from('work_orders')
    .select(`id,assigned_to,created_by,status,total_amount,cancelled_at,${FINISHED_DATE_COLUMNS.join(',')}`)
    .eq('company_id', companyId)
    .in('status', [...PROFITABILITY_FINISHED_STATUSES, WORK_ORDER_STATUS.CANCELLED])
    // Superset on the server (any candidate date in range); the exact rule is applied below.
    .or([...FINISHED_DATE_COLUMNS, 'cancelled_at'].map(inRange).join(','));
  if (error) return { error, finished: [], cancelled: [] };
  const fromMs = new Date(startISO).getTime();
  const toMs = new Date(endISO).getTime();
  const within = (at) => {
    if (!at) return false;
    const t = new Date(at).getTime();
    return t >= fromMs && t <= toMs;
  };
  const rows = data || [];
  return {
    error: null,
    finished: rows.filter((wo) => PROFITABILITY_FINISHED_STATUSES.includes(wo.status) && within(jobFinishedAt(wo))),
    cancelled: rows.filter((wo) => wo.status === WORK_ORDER_STATUS.CANCELLED && within(wo.cancelled_at)),
  };
};

// Completion Rate: the Tech Scorecards' rule company-wide, finished / (finished + cancelled)
// in the period. It divided completed-by-created_at by every job created in the range.
const computeCompletionRate = ({ finished, cancelled }) => {
  const denom = finished.length + cancelled.length;
  return denom > 0 ? Math.round((finished.length / denom) * 100) : 0;
};

// 5) Unscheduled Jobs
const fetchUnscheduledJobs = async (companyId) => {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('work_orders')
    .select('id', { count: 'exact' })
    .eq('company_id', companyId)
    .in('status', [WORK_ORDER_STATUS.SCHEDULED, WORK_ORDER_STATUS.IN_PROGRESS]);
  return error ? 0 : (data?.length || 0);
};

// 6) Outstanding Invoices & A/R Aging
const fetchOutstandingInvoices = async (companyId) => {
  const supabase = getSupabaseClient();
  const { data: invoicesData, error: invoicesError } = await supabase
    .from('invoices')
    .select('id,total_amount,amount_paid,status,issue_date,due_date')
    .eq('company_id', companyId);
  
  let outstandingInvoices = 0;
  let arAging = { bucket0_30: 0, bucket31_60: 0, bucket61_90: 0, bucket90_plus: 0 };
  // Board #3911. The A/R Aging card's "Overdue Invoices" line summed bucket61_90 +
  // bucket90_plus, which silently drops everything overdue by 1-60 days: live it showed
  // $5,982.88 beside an Action Required card reading $6,582.88, the $600 difference being
  // one invoice due 7 days earlier. The first bucket cannot simply be added instead,
  // because by design it also holds not-yet-due and null-due invoices (daysOld clamps to 0
  // above). So the genuinely past-due figure is accumulated here, from the same openInvs
  // and the same balance, and therefore reconciles with the buckets by construction.
  let overdueTotal = 0;
  let overdueCount = 0;
  
  if (!invoicesError && invoicesData) {
    const allInvs = invoicesData;
    const openInvs = allInvs.filter(inv => isReceivableInvoice(inv.status));
    const ids = openInvs.map(i => i.id);
    let paymentsByInvoice = new Map();
    
    if (ids.length > 0) {
      const { data: paymentsByInvoiceData, error: paymentsByInvoiceError } = await supabase
        .from('payments')
        .select('invoice_id,amount')
        .in('status', ['completed', 'partially_refunded'])
        .in('invoice_id', ids);
      
      if (!paymentsByInvoiceError && paymentsByInvoiceData) {
        const pays = paymentsByInvoiceData;
        pays.forEach(p => {
          const k = p.invoice_id;
          const prev = paymentsByInvoice.get(k) || 0;
          paymentsByInvoice.set(k, prev + Number(p.amount || 0));
        });
      }
    }
    
    // Company-timezone today, as a plain YYYY-MM-DD. due_date is a `date` column, so
    // comparing two date strings parsed at UTC midnight gives exact whole days with no
    // timezone drift either way — the trap CLAUDE.md §19 describes, where a `date` run
    // through a timestamp helper lands a day earlier for every tenant west of UTC.
    const todayMs = Date.parse(companyDateKey());
    const msDay = 24*60*60*1000;
    openInvs.forEach(inv => {
      const paid = paymentsByInvoice.get(inv.id) || 0;
      const balance = Math.max(0, Number(inv.total_amount || 0) - paid);
      outstandingInvoices += balance;
      // Age by DUE DATE, which is what this card's own drill-down uses and what the
      // database's invoice_aging_report view uses (its buckets are literally named
      // overdue_31_60_days). This aged by issue_date instead, so ">90 days" on the card
      // and "90+" on the Invoices page it links to were measuring from different events
      // and could not be reconciled — the same figure, two clocks.
      //
      // A missing due date counts as 0 days, which keeps it in the first bucket rather
      // than dropping it: the drill-down treats not-yet-due and null-due invoices as
      // current for the same reason, and the aging total has to keep reconciling with
      // total outstanding A/R.
      const ageRef = inv.due_date;
      const daysOld = ageRef ? Math.max(0, Math.floor((todayMs - Date.parse(String(ageRef).slice(0, 10))) / msDay)) : 0;
      // Strictly past due, and only when a due date exists — a null or future due date is
      // not overdue, which is the distinction the bucket maths cannot express.
      if (ageRef && Date.parse(String(ageRef).slice(0, 10)) < todayMs && balance > 0) {
        overdueTotal += balance;
        overdueCount += 1;
      }
      if (daysOld <= 30) arAging.bucket0_30 += balance;
      else if (daysOld <= 60) arAging.bucket31_60 += balance;
      else if (daysOld <= 90) arAging.bucket61_90 += balance;
      else arAging.bucket90_plus += balance;
    });
  }
  
  return { outstandingInvoices, arAging, overdueTotal, overdueCount };
};

// 7) Quote Conversion Rate
// "Sent" = quotes with status 'sent' (proposal sent to customer)
// "Accepted" = quotes that were approved (customer accepted)
const fetchQuoteConversion = async (companyId, startISO, endISO) => {
  const supabase = getSupabaseClient();
  
  // Count only 'sent' as "sent quotes" (not 'approved')
  const { data: sentQuotesData, error: sentQuotesError } = await supabase
    .from('work_orders')
    .select('id', { count: 'exact' })
    .eq('company_id', companyId)
    .eq('status', WORK_ORDER_STATUS.SENT)
    .gte('created_at', startISO)
    .lte('created_at', endISO);

  // Count 'approved' as "accepted quotes"
  const { data: acceptedQuotesData, error: acceptedQuotesError } = await supabase
    .from('work_orders')
    .select('id', { count: 'exact' })
    .eq('company_id', companyId)
    .eq('status', WORK_ORDER_STATUS.APPROVED)
    .gte('created_at', startISO)
    .lte('created_at', endISO);

  const sentQuotes = sentQuotesError ? 0 : (sentQuotesData?.length || 0);
  const acceptedQuotes = acceptedQuotesError ? 0 : (acceptedQuotesData?.length || 0);
  const quoteConversionRate = sentQuotes > 0 ? Math.round((acceptedQuotes / sentQuotes) * 100) : 0;
  
  return { sentQuotes, acceptedQuotes, quoteConversionRate };
};

// 8) Overdue Jobs
const fetchOverdueJobs = async (companyId) => {
  const supabase = getSupabaseClient();
  const todayISO = new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate(), 0, 0, 0, 0).toISOString();
  
  const { data, error } = await supabase
    .from('work_orders')
    .select('id', { count: 'exact' })
    .eq('company_id', companyId)
    .lt('scheduled_end', todayISO)
    .in('status', [WORK_ORDER_STATUS.SCHEDULED, WORK_ORDER_STATUS.IN_PROGRESS]);

  return error ? 0 : (data?.length || 0);
};

// 9) Funnel Counts
const fetchFunnel = async (companyId, startISO, endISO) => {
  const supabase = getSupabaseClient();
  
  const [sentResult, acceptedResult, scheduledResult, inProgressResult, completedResult] = await Promise.all([
    supabase.from('work_orders').select('id', { count: 'exact' }).eq('company_id', companyId).eq('status', WORK_ORDER_STATUS.SENT).gte('created_at', startISO).lte('created_at', endISO),
    supabase.from('work_orders').select('id', { count: 'exact' }).eq('company_id', companyId).eq('status', WORK_ORDER_STATUS.APPROVED).gte('created_at', startISO).lte('created_at', endISO),
    supabase.from('work_orders').select('id', { count: 'exact' }).eq('company_id', companyId).eq('status', WORK_ORDER_STATUS.SCHEDULED).gte('created_at', startISO).lte('created_at', endISO),
    supabase.from('work_orders').select('id', { count: 'exact' }).eq('company_id', companyId).eq('status', WORK_ORDER_STATUS.IN_PROGRESS).gte('created_at', startISO).lte('created_at', endISO),
    supabase.from('work_orders').select('id,created_by').eq('company_id', companyId).eq('status', WORK_ORDER_STATUS.COMPLETED).gte('created_at', startISO).lte('created_at', endISO)
  ]);
  
  return {
    sent: sentResult.error ? 0 : (sentResult.data?.length || 0),
    accepted: acceptedResult.error ? 0 : (acceptedResult.data?.length || 0),
    scheduled: scheduledResult.error ? 0 : (scheduledResult.data?.length || 0),
    inProgress: inProgressResult.error ? 0 : (inProgressResult.data?.length || 0),
    completed: completedResult.error ? 0 : (completedResult.data?.length || 0),
    completedJobsData: completedResult.data || []
  };
};

// 10) Leaderboard: counts the work finished in the period (fetchFinishedWorkInPeriod).
const fetchLeaderboard = async (companyId, completedJobsData) => {
  let leaderboard = [];
  
  if (completedJobsData && completedJobsData.length > 0) {
    const counts = new Map();
    completedJobsData.forEach(j => {
      if (!j.created_by) return;
      counts.set(j.created_by, (counts.get(j.created_by) || 0) + 1);
    });
    
    leaderboard = Array.from(counts.entries())
      .map(([techId, count]) => ({ techId, count }))
      .sort((a,b) => b.count - a.count)
      .slice(0, 5);
  }
  
  // Resolve technician names
  if (leaderboard.length) {
    const supabase = getSupabaseClient();
    const techIds = leaderboard.map(t => t.techId);
    const { data: usersData, error: usersError } = await supabase
      .from('users')
      .select('id,name,first_name,last_name')
      .in('id', techIds);
    
    if (!usersError && usersData) {
      const nameById = new Map(usersData.map(u => [u.id, u.name || [u.first_name, u.last_name].filter(Boolean).join(' ') || currentTerm('worker')]));
      leaderboard = leaderboard.map(t => ({ ...t, name: nameById.get(t.techId) || t.techId }));
    }
  }
  
  return leaderboard;
};

// 10b) Tech Scorecards
const fetchTechScoreCards = async (companyId, finishedWork, start, end) => {
  const supabase = getSupabaseClient();
  
  // Work FINISHED or CANCELLED in the period, by when that happened (audit 2026-10-17). It
  // keyed on created_at and counted status === 'completed' only, so a job dropped off the
  // scorecard the moment it was invoiced: 3 rows / $600 against 7 techs / ~$5,678 finished.
  // The rows come from fetchFinishedWorkInPeriod, the definition Completion Rate shares.
  const techJobsError = finishedWork.error;
  const techJobsData = [
    ...finishedWork.finished.map((wo) => ({ ...wo, finished: true })),
    ...finishedWork.cancelled,
  ];
  
  const { data: empsData, error: empsError } = await supabase
    .from('employees')
    .select('id,user_id,first_name,last_name')
    .eq('company_id', companyId);
  // #4834: this is a NAME lookup, not a scheduling gate. Filtering is_schedulable=true made every
  // job assigned to a non-schedulable employee render as "Unknown Tech" (live: Mike Maxwell,
  // employee da9c92dc, is_schedulable=false, 19 work orders assigned).
  
  const techScoreCards = [];
  
  if (!techJobsError && techJobsData && techJobsData.length > 0) {
    const techMap = new Map();
    techJobsData.forEach(wo => {
      if (!wo.assigned_to) return;
      if (!techMap.has(wo.assigned_to)) {
        techMap.set(wo.assigned_to, { completed: 0, cancelled: 0, total: 0, revenue: 0 });
      }
      const stats = techMap.get(wo.assigned_to);
      stats.total++;
      if (wo.finished) {
        stats.completed++;
        stats.revenue += Number(wo.total_amount) || 0;
      } else if (wo.status === WORK_ORDER_STATUS.CANCELLED) {
        stats.cancelled++;
      }
    });
    
    const weekdays = countWeekdays(start, end);
    
    const nameByEmpId = new Map();
    if (!empsError && empsData) {
      empsData.forEach(emp => {
        const name = getDisplayName(emp);
        nameByEmpId.set(emp.id, name);
        if (emp.user_id) nameByEmpId.set(emp.user_id, name);
      });
    }
    
    techMap.forEach((stats, techId) => {
      const name = nameByEmpId.get(techId) || 'Unknown Tech';
      const completed = stats.completed;
      const revenue = stats.revenue;
      const avgTicket = completed > 0 ? revenue / completed : 0;
      const completionDenom = stats.completed + stats.cancelled;
      // null renders "—": nothing finished or cancelled is not a 0% completion rate.
      const completionRate = completionDenom > 0 ? Math.round((stats.completed / completionDenom) * 100) : null;
      // #4833: one decimal rounded 1 job / 22 weekdays to "0.0" — a tech with real work read as idle.
      const jobsPerDay = weekdays > 0 ? (completed / weekdays).toFixed(2) : '0.00';
      techScoreCards.push({
        id: techId,
        name,
        completed,
        revenue,
        avgTicket,
        completionRate,
        jobsPerDay
      });
    });
  }
  
  return techScoreCards;
};

// 11) Team Utilization
const fetchTeamUtilization = async (companyId, startISO, endISO, start, end, activeEmployees) => {
  const supabase = getSupabaseClient();
  
  const { data: woDurData, error: woDurError } = await supabase
    .from('work_orders')
    .select('created_at,updated_at,scheduled_start,scheduled_end')
    .eq('company_id', companyId)
    .in('status', [WORK_ORDER_STATUS.SCHEDULED, WORK_ORDER_STATUS.IN_PROGRESS, WORK_ORDER_STATUS.COMPLETED])
    .gte('scheduled_start', startISO)
    .lte('scheduled_start', endISO);
  
  let scheduledHours = 0;
  if (!woDurError && woDurData) {
    const rows = woDurData;
    rows.forEach(r => {
      if (r.scheduled_start && r.scheduled_end) {
        const st = new Date(r.scheduled_start);
        const et = new Date(r.scheduled_end);
        const h = Math.max(0, (et - st) / 3600000);
        scheduledHours += h;
      }
    });
  }
  
  // #2267: capacity on the same basis as the Calendar's Utilization tile (CalendarStats):
  // SCHEDULABLE employees x the company's configured hours for each day in the range. This
  // was every active employee (office staff included) x a hardcoded 8h x weekdays, so the
  // denominator was inflated and ignored the company's real working days and hours.
  const { count: schedulable } = await supabase
    .from('employees')
    .select('id', { count: 'exact', head: true })
    .eq('company_id', companyId)
    .eq('status', 'active')
    .eq('is_schedulable', true);
  const { business_hours: hours } = await getSchedulingSettings(companyId, { fresh: false });
  const hoursForDay = (d) => {
    const day = hours?.[DAY_NAMES[d.getDay()]];
    if (!day?.open || !day.start || !day.end) return 0;
    const [sh, sm] = day.start.split(':').map(Number);
    const [eh, em] = day.end.split(':').map(Number);
    return Math.max(0, (eh * 60 + em - (sh * 60 + sm)) / 60);
  };
  let hoursPerEmployee = 0;
  const cursor = new Date(start); cursor.setHours(0, 0, 0, 0);
  const lastDay = new Date(end); lastDay.setHours(0, 0, 0, 0);
  while (cursor <= lastDay) {
    hoursPerEmployee += hoursForDay(cursor);
    cursor.setDate(cursor.getDate() + 1);
  }
  const capacityHours = (schedulable || 0) * hoursPerEmployee;
  const utilizationPct = capacityHours > 0 ? Math.round((scheduledHours / capacityHours) * 100) : 0;
  
  return { scheduledHours, capacityHours, utilizationPct };
};

// 12) Operations Scorecard Metrics
const fetchOperationsScorecard = async (companyId, startISO, endISO) => {
  const supabase = getSupabaseClient();
  
  // On-Time %
  const { data: onTimeData, error: onTimeError } = await supabase
    .from('work_orders')
    .select('id,scheduled_end,completed_at')
    .eq('company_id', companyId)
    // #2266: 'completed' is transient — a job leaves it when invoiced — so this sampled only
    // jobs stuck before invoicing and usually found none. Finished = completed/invoiced/paid/closed.
    .in('status', FINISHED_JOB_STATUSES)
    .not('scheduled_end', 'is', null)
    .not('completed_at', 'is', null)
    // #2266, bounced live 2026-09-27: windowed on COMPLETION, not creation. A completion rate
    // measures jobs finished in the period; keyed on created_at it dropped every job created
    // before the window and finished inside it. Live on Jerry's: 6 finished jobs were created in
    // the last 30 days and none had a schedule (so this read "—"), while 14 were COMPLETED in it
    // and 7 of those were scheduled.
    .gte('completed_at', startISO)
    .lte('completed_at', endISO);

  let onTimeCount = 0;
  let totalOnTimeCheck = 0;
  if (!onTimeError && onTimeData) {
    onTimeData.forEach(wo => {
      if (wo.scheduled_end && wo.completed_at) {
        const scheduledEnd = new Date(wo.scheduled_end);
        const completedAt = new Date(wo.completed_at);
        const diffMs = completedAt - scheduledEnd;
        totalOnTimeCheck++;
        if (diffMs <= 60 * 60 * 1000) {
          onTimeCount++;
        }
      }
    });
  }
  const onTimePct = totalOnTimeCheck > 0 ? Math.round((onTimeCount / totalOnTimeCheck) * 100) : 0;
  
  // Backlog Aging
  const { data: backlogData, error: backlogError } = await supabase
    .from('work_orders')
    .select('id,created_at')
    .eq('company_id', companyId)
    .in('status', [WORK_ORDER_STATUS.APPROVED, WORK_ORDER_STATUS.SCHEDULED])
    .is('scheduled_start', null);
  
  let backlogAging = 0;
  if (!backlogError && backlogData && backlogData.length > 0) {
    const nowBacklog = new Date();
    const totalDays = backlogData.reduce((sum, wo) => {
      const created = new Date(wo.created_at);
      const days = (nowBacklog - created) / (24 * 60 * 60 * 1000);
      return sum + days;
    }, 0);
    backlogAging = (totalDays / backlogData.length).toFixed(1);
  }
  
  // Cancellation Rate — a COHORT measure, and deliberately a different question from the
  // "Cancelled" money tile above it (board #1348).
  //
  // Both sides are keyed on created_at: of the jobs CREATED in this window, what share is now
  // cancelled. That is a coherent statement and it is why the two tiles can legitimately read
  // "$12,500 cancelled" and "0%" at the same time — the cancelled money belongs to jobs created
  // BEFORE the window, while none of the 30 jobs created inside it has been cancelled (measured
  // live 2026-09-21). Nothing was wrong with this calculation; what was wrong was that neither
  // tile said which question it answered, so together they read as a contradiction. Both now
  // state their scope on screen. Do not "align" these by mixing the columns: a numerator keyed on
  // when a cancellation HAPPENED over a denominator keyed on when a job was CREATED can exceed
  // 100%, which is how this kind of tidy-up usually goes wrong.
  const [cancelledResult, totalJobsResult] = await Promise.all([
    supabase.from('work_orders').select('id', { count: 'exact' }).eq('company_id', companyId).eq('status', WORK_ORDER_STATUS.CANCELLED).gte('created_at', startISO).lte('created_at', endISO),
    supabase.from('work_orders').select('id', { count: 'exact' }).eq('company_id', companyId).gte('created_at', startISO).lte('created_at', endISO)
  ]);
  
  const cancelledCount = cancelledResult.error ? 0 : (cancelledResult.data?.length || 0);
  const totalJobsCount = totalJobsResult.error ? 0 : (totalJobsResult.data?.length || 0);
  const cancellationRate = totalJobsCount > 0 ? Math.round((cancelledCount / totalJobsCount) * 100) : 0;
  
  // No-Show Count
  const twoHoursAgo = new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString();
  const { data: noShowData, error: noShowError } = await supabase
    .from('work_orders')
    .select('id', { count: 'exact' })
    .eq('company_id', companyId)
    .eq('status', WORK_ORDER_STATUS.SCHEDULED)
    .lt('scheduled_start', twoHoursAgo);
  const noShowCount = noShowError ? 0 : (noShowData?.length || 0);
  
  // Average Job Duration
  const { data: durationData, error: durationError } = await supabase
    .from('work_orders')
    .select('id,actual_start,actual_end,completed_at')
    .eq('company_id', companyId)
    .in('status', FINISHED_JOB_STATUSES) // #2266, same reason as On-Time above
    // Time actually on the job. scheduled_start -> completed_at counted every day a job waited
    // between its slot and being marked done: 4446.2h average, live 2026-10-17 (audit). No
    // actual start/end recorded = nothing to average, and the tile shows "—".
    .not('actual_start', 'is', null)
    .not('actual_end', 'is', null)
    // Windowed on completion for the same reason as On-Time above (#2266).
    .gte('completed_at', startISO)
    .lte('completed_at', endISO);

  let avgJobDuration = 0;
  if (!durationError && durationData && durationData.length > 0) {
    const totalHours = durationData.reduce((sum, wo) => {
      const start = new Date(wo.actual_start);
      const end = new Date(wo.actual_end);
      const hours = Math.max(0, (end - start) / 3600000);
      return sum + hours;
    }, 0);
    avgJobDuration = (totalHours / durationData.length).toFixed(1);
  }
  
  // Quote-to-Job Conversion. #2266: this divided rows CURRENTLY 'approved' by rows CURRENTLY
  // 'sent' — both transient (CLAUDE.md §8), so an accepted quote that got scheduled counted in
  // neither and the card showed "—". Same cohort as utils/revenueFunnel: every quote issued
  // (has a quote_number) in the range; won = quote_accepted_at or an accepted/advanced status.
  const { data: quoteRows, error: quoteRowsError } = await supabase
    .from('work_orders')
    .select('id,status,quote_accepted_at,quote_sent_at')
    .eq('company_id', companyId)
    .not('quote_number', 'is', null)
    .gte('created_at', startISO)
    .lte('created_at', endISO);
  // Quotes the customer actually got (operator ruling 2026-10-17): a draft never sent cannot
  // convert, and counting 49 unsent drafts made it read 14%. Sent = quote_sent_at, or any
  // status past draft (a quote agreed in person goes draft -> approved without a send).
  const issuedQuotes = (quoteRowsError ? [] : (quoteRows || []))
    .filter(q => q.quote_sent_at
      || ![WORK_ORDER_STATUS.DRAFT, WORK_ORDER_STATUS.CANCELLED].includes(String(q.status || '').toLowerCase()));
  const sentQuotesCount = issuedQuotes.length;
  const acceptedJobsCount = issuedQuotes.filter(q => q.quote_accepted_at || isAcceptedQuote(q.status)).length;
  const quoteToJobConversion = sentQuotesCount > 0 ? Math.round((acceptedJobsCount / sentQuotesCount) * 100) : 0;
  
  // Companion sample sizes so the UI can show "—" (no data yet) instead of a
  // misleading 0% / 0h when a metric's denominator is zero.
  return {
    onTimePct, backlogAging, cancellationRate, noShowCount, avgJobDuration, quoteToJobConversion,
    onTimeSampleCount: totalOnTimeCheck,
    backlogCount: (backlogError ? 0 : (backlogData?.length || 0)),
    totalJobsCount,
    durationSampleCount: (durationError ? 0 : (durationData?.length || 0)),
    sentQuotesCount,
  };
};

// 13) Dispatch Control - Unassigned Jobs
const fetchUnassignedJobs = async (companyId) => {
  const supabase = getSupabaseClient();
  
  // count: 'exact' so the header says how many there are, not how many fit in the list — it
  // read "(10)" against 33 unassigned live (audit 2026-10-17).
  const { data: unassignedData, error: unassignedError, count: unassignedTotal } = await supabase
    .from('work_orders')
    .select('id, title, customer_id, total_amount, created_at, status', { count: 'exact' })
    .is('assigned_to', null)
    .in('status', [WORK_ORDER_STATUS.APPROVED, WORK_ORDER_STATUS.SCHEDULED, WORK_ORDER_STATUS.IN_PROGRESS])
    .eq('company_id', companyId)
    .order('created_at', { ascending: true })
    .limit(10);
  
  const unassignedJobs = unassignedError ? [] : (unassignedData || []);
  
  // Batch fetch customer names
  if (unassignedJobs.length > 0) {
    const custIds = unassignedJobs.map(j => j.customer_id).filter(Boolean);
    if (custIds.length > 0) {
      const { data: customers } = await supabase.from('customers').select('id, name').in('id', custIds);
      if (customers) {
        const customerMap = {};
        customers.forEach(c => { customerMap[c.id] = c.name; });
        unassignedJobs.forEach(j => { j.customerName = customerMap[j.customer_id] || 'Unknown'; });
      }
    }
  }
  
  return { unassignedJobs, unassignedCount: unassignedError ? 0 : (unassignedTotal ?? unassignedJobs.length) };
};

// 14) Available Techs
const fetchAvailableTechs = async (companyId) => {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from('employees')
    .select('id')
    .eq('company_id', companyId)
    .eq('status', 'active')
    .eq('is_schedulable', true);
  return error ? 0 : (data?.length || 0);
};

// 15) Capacity Heatmap
const fetchCapacityHeatmap = async (companyId, availableTechs) => {
  const supabase = getSupabaseClient();
  
  // Company calendar days (audit 2026-10-17): keys came from toISOString / the raw UTC
  // timestamp, so a job after 8pm Eastern landed on the next day's square.
  const addDays = (ymd, n) => {
    const [y, m, d] = ymd.split('-').map(Number);
    const t = new Date(Date.UTC(y, m - 1, d + n));
    return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}-${String(t.getUTCDate()).padStart(2, '0')}`;
  };
  const todayKey = companyDateKey(new Date());
  const dispatchTodayISO = companyWallClockToUtc(todayKey, '00:00');
  const sevenDaysISO = companyWallClockToUtc(addDays(todayKey, 7), '00:00');
  
  const { data: scheduledData, error: scheduledError } = await supabase
    .from('work_orders')
    .select('scheduled_start, scheduled_end, assigned_to')
    .in('status', [WORK_ORDER_STATUS.SCHEDULED, WORK_ORDER_STATUS.IN_PROGRESS, WORK_ORDER_STATUS.APPROVED])
    .gte('scheduled_start', dispatchTodayISO)
    .lt('scheduled_start', sevenDaysISO)
    .eq('company_id', companyId);
  
  const capacityHeatmap = [];
  for (let i = 0; i < 7; i++) {
    const dayStr = addDays(todayKey, i);
    const dayDate = new Date(companyWallClockToUtc(dayStr, '12:00'));
    const dayName = fmtDate(dayDate, { weekday: 'short' });
    
    let jobCount = 0;
    let totalHours = 0;
    if (!scheduledError && scheduledData) {
      scheduledData.forEach(wo => {
        if (wo.scheduled_start) {
          const woDate = companyDateKey(new Date(wo.scheduled_start));
          if (woDate === dayStr) {
            jobCount++;
            if (wo.scheduled_start && wo.scheduled_end) {
              const st = new Date(wo.scheduled_start);
              const et = new Date(wo.scheduled_end);
              totalHours += Math.max(0, (et - st) / 3600000);
            }
          }
        }
      });
    }
    
    const techCapacity = availableTechs * 8;
    const fillRate = techCapacity > 0 ? Math.round((totalHours / techCapacity) * 100) : 0;
    // An empty day (nothing scheduled) reads neutral — not a warning. Only color
    // by utilization when there's actual work booked. Includes dark-theme variants.
    let colorClass = 'bg-gray-100 text-gray-700 dark:bg-slate-700/60 dark:text-gray-300';
    if (jobCount > 0) {
      if (fillRate < 40) colorClass = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300';
      else if (fillRate > 90) colorClass = 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300';
      else colorClass = 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300';
    }
    
    capacityHeatmap.push({
      date: dayStr,
      dayName,
      jobCount,
      totalHours: Math.round(totalHours * 10) / 10,
      fillRate,
      colorClass
    });
  }
  
  return capacityHeatmap;
};

// 16) Revenue Pipeline
const fetchRevenuePipeline = async (companyId, startISO, endISO) => {
  const supabase = getSupabaseClient();
  const pipelineNow = new Date();
  const fourWeeksFromNow = new Date(pipelineNow.getTime() + 28 * 24 * 60 * 60 * 1000);
  
  // Booked Revenue (Next 4 Weeks)
  const { data: bookedData } = await supabase
    .from('work_orders')
    .select('total_amount')
    .eq('company_id', companyId)
    .in('status', [WORK_ORDER_STATUS.SCHEDULED, WORK_ORDER_STATUS.IN_PROGRESS, WORK_ORDER_STATUS.APPROVED])
    .gte('scheduled_start', pipelineNow.toISOString())
    .lte('scheduled_start', fourWeeksFromNow.toISOString());
  const bookedRevenue = bookedData?.reduce((sum, wo) => sum + parseFloat(wo.total_amount || 0), 0) || 0;
  
  // Outstanding Invoices — issued, unpaid A/R only (no drafts/voids), net of payments.
  const { data: outstandingInvData } = await supabase
    .from('invoices')
    .select('total_amount, amount_paid, status')
    .eq('company_id', companyId)
    .in('status', AR_INVOICE_STATUSES);
  const outstandingInvoiceAmount = outstandingInvData?.reduce(
    (sum, inv) => sum + Math.max(0, parseFloat(inv.total_amount || 0) - parseFloat(inv.amount_paid || 0)), 0) || 0;
  
  // Missed Revenue — money on jobs CANCELLED in this period.
  //
  // Board #1348. This keyed on `updated_at`, which is "last touched by anything", not "cancelled".
  // So a job cancelled a year ago counted as this month's missed revenue the moment somebody
  // opened and saved it, and the tile's value drifted upward with ordinary editing. Measured live
  // 2026-09-21 on 48f32d34, last 30 days: by updated_at $32,160 — six cancelled jobs, all of them
  // edited recently — against $12,500 by the date they were actually cancelled. The reported
  // $12,500 was the true figure; the number has been inflating since.
  //
  // `cancelled_at` is the real column and is populated (6 of 6 on this company, 6 of 8 platform
  // wide). The two rows without it are older, so `updated_at` stays as the fallback for those
  // ONLY — dropping them would silently under-report rather than over-report, which is not an
  // improvement, just a different lie.
  const cancelledWindow =
    `and(cancelled_at.gte.${startISO},cancelled_at.lte.${endISO}),` +
    `and(cancelled_at.is.null,updated_at.gte.${startISO},updated_at.lte.${endISO})`;
  const { data: cancelledData } = await supabase
    .from('work_orders')
    .select('total_amount')
    .eq('company_id', companyId)
    .eq('status', WORK_ORDER_STATUS.CANCELLED)
    .or(cancelledWindow);
  const missedRevenue = cancelledData?.reduce((sum, wo) => sum + parseFloat(wo.total_amount || 0), 0) || 0;
  
  // Uninvoiced Completed. #4840: same predicate as the Jobs page "Needs Invoice" card
  // (JobsStats.js / get_job_statistics): status='completed'. The extra invoices lookup made the
  // two surfaces disagree; a job that has been invoiced is no longer 'completed' in this pipeline.
  const { data: completedWO } = await supabase
    .from('work_orders')
    .select('total_amount')
    .eq('company_id', companyId)
    .eq('status', WORK_ORDER_STATUS.COMPLETED);
  const uninvoicedCompleted = completedWO?.reduce((sum, wo) => sum + parseFloat(wo.total_amount || 0), 0) || 0;
  
  return { bookedRevenue, outstandingInvoiceAmount, missedRevenue, uninvoicedCompleted };
};

// ============== MAIN FETCH FUNCTION ==============
export const fetchAdminDashboardDataLegacy = async ({ companyId, range }) => {
  if (!companyId) {
    throw new Error('Company ID is required');
  }
  
  const supabase = getSupabaseClient();
  const now = new Date();
  const presets = {
    TODAY: () => {
      const s = new Date(now); s.setHours(0,0,0,0);
      const e = new Date(now); e.setHours(23,59,59,999);
      return { start: s, end: e };
    },
    WEEK: () => {
      const s = new Date(now); s.setHours(0,0,0,0); s.setDate(now.getDate() - now.getDay());
      const e = new Date(s); e.setDate(s.getDate() + 6); e.setHours(23,59,59,999);
      return { start: s, end: e };
    },
    MONTH: () => {
      const s = new Date(now.getFullYear(), now.getMonth(), 1); s.setHours(0,0,0,0);
      const e = new Date(now.getFullYear(), now.getMonth() + 1, 0); e.setHours(23,59,59,999);
      return { start: s, end: e };
    },
    LAST_30: () => {
      const s = new Date(now); s.setHours(0,0,0,0); s.setDate(now.getDate() - 30);
      const e = new Date(now); e.setHours(23,59,59,999);
      return { start: s, end: e };
    }
  };
  
  const { start, end } = range.start && range.end ? range : (presets[range.preset] || presets.LAST_30)();
  // The chosen days, bounded at the COMPANY's midnights (the revenue chart buckets by company day).
  const dayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const startISO = companyWallClockToUtc(dayKey(start), '00:00');
  const endISO = new Date(new Date(companyWallClockToUtc(addDaysToDateKey(dayKey(end), 1), '00:00')).getTime() - 1).toISOString();
  
  // Run parallel fetches - FIRST BATCH (independent)
  const [
    activeEmployees,
    activeJobs,
    jobsInRange,
    { cashRevenue, revenueSeries },
    { bookedRevenue, outstandingInvoiceAmount, missedRevenue, uninvoicedCompleted },
    finishedWork
  ] = await Promise.all([
    fetchActiveEmployees(companyId),
    fetchActiveJobs(companyId),
    fetchJobsInRange(companyId, startISO, endISO),
    fetchCashRevenue(companyId, startISO, endISO),
    fetchRevenuePipeline(companyId, startISO, endISO),
    fetchFinishedWorkInPeriod(companyId, startISO, endISO)
  ]);
  
  const completionRate = computeCompletionRate(finishedWork);
  
  const basicKPIs = {
    activeEmployees,
    // Two different questions that were sharing one field — and therefore one answer, shown
    // twice under two headings that promised different things.
    activeJobs,
    jobsThisWeek: jobsInRange,
    monthlyRevenue: cashRevenue,
    // #5556: the calendar days the dashboard counted over, so a tile can open Reports on the
    // same window (local calendar day of the same start/end used for startISO/endISO).
    rangeStartDay: `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}-${String(start.getDate()).padStart(2, '0')}`,
    rangeEndDay: `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`,
    completionRate,
    revenueSeries,
    bookedRevenue,
    outstandingInvoiceAmount,
    uninvoicedCompleted,
    missedRevenue
  };
  
  // Advanced KPIs - FIRST BATCH (independent)
  const [
    unscheduledJobs,
    { outstandingInvoices, arAging, overdueTotal, overdueCount },
    { quoteConversionRate, sentQuotes, acceptedQuotes },
    overdueJobs,
    { sent, accepted, scheduled, inProgress, completed },
    techScoreCards,
    { scheduledHours, capacityHours, utilizationPct },
    { onTimePct, backlogAging, cancellationRate, noShowCount, avgJobDuration, quoteToJobConversion,
      onTimeSampleCount, backlogCount, totalJobsCount, durationSampleCount, sentQuotesCount },
    { unassignedJobs, unassignedCount }
  ] = await Promise.all([
    fetchUnscheduledJobs(companyId),
    fetchOutstandingInvoices(companyId),
    fetchQuoteConversion(companyId, startISO, endISO),
    fetchOverdueJobs(companyId),
    fetchFunnel(companyId, startISO, endISO),
    fetchTechScoreCards(companyId, finishedWork, start, end),
    fetchTeamUtilization(companyId, startISO, endISO, start, end, activeEmployees),
    fetchOperationsScorecard(companyId, startISO, endISO),
    fetchUnassignedJobs(companyId)
  ]);
  
  // BATCH 2: capacityHeatmap depends on availableTechs
  const availableTechs = await fetchAvailableTechs(companyId);
  const leaderboard = await fetchLeaderboard(companyId, finishedWork.finished);
  const capacityHeatmap = await fetchCapacityHeatmap(companyId, availableTechs);
  
  return {
    ...basicKPIs,
    unscheduledJobs,
    outstandingInvoices,
    quoteConversionRate,
    sentQuotes,
    acceptedQuotes,
    overdueJobs,
    arAging,
    // #3911: the genuinely past-due receivable, for the A/R Aging card's Overdue line.
    overdueTotal,
    overdueCount,
    utilizationPct,
    scheduledHours,
    capacityHours,
    funnel: { sent, accepted, scheduled, inProgress, completed },
    leaderboard,
    techScoreCards,
    onTimePct,
    backlogAging,
    cancellationRate,
    noShowCount,
    avgJobDuration,
    quoteToJobConversion,
    onTimeSampleCount,
    backlogCount,
    totalJobsCount,
    durationSampleCount,
    sentQuotesCount,
    unassignedJobs,
    unassignedCount,
    capacityHeatmap,
    availableTechs,
    loading: false
  };
};

// ============== RECENT ACTIVITY HOOK ==============
export const fetchRecentActivity = async (companyId) => {
  if (!companyId) return [];
  
  const supabase = getSupabaseClient();
  
  const [workOrdersResult, quotesResult, paymentsResult, invoicesResult] = await Promise.all([
    supabase.from('work_orders').select('id,title,status,updated_at,customer_id').eq('company_id', companyId).in('status', [WORK_ORDER_STATUS.SCHEDULED, WORK_ORDER_STATUS.COMPLETED]).order('updated_at', { ascending: false }).limit(10),
    supabase.from('work_orders').select('id,title,status,updated_at,customer_id').eq('company_id', companyId).in('status', [WORK_ORDER_STATUS.SENT, WORK_ORDER_STATUS.CHANGES_REQUESTED]).order('updated_at', { ascending: false }).limit(10),
    supabase.from('payments').select('id,amount,status,updated_at').eq('company_id', companyId).in('status', ['completed', 'partially_refunded', 'refunded', 'disputed']).order('updated_at', { ascending: false }).limit(10),
    supabase.from('invoices').select('id,invoice_number,total_amount,status,sent_at,updated_at').eq('company_id', companyId).in('status', [INVOICE_STATUS.SENT, INVOICE_STATUS.VIEWED]).order('updated_at', { ascending: false }).limit(10)
  ]);
  
  // Batch fetch customers
  const allCustomerIds = [
    ...(workOrdersResult.data || []).map(wo => wo.customer_id).filter(Boolean),
    ...(quotesResult.data || []).map(q => q.customer_id).filter(Boolean)
  ];
  const uniqueCustomerIds = [...new Set(allCustomerIds)];
  
  let customerMap = {};
  if (uniqueCustomerIds.length > 0) {
    const { data: customers } = await supabase.from('customers').select('id,name').in('id', uniqueCustomerIds);
    if (customers) {
      customers.forEach(c => { customerMap[c.id] = { name: c.name }; });
    }
  }
  
  // Merge customers
  if (workOrdersResult.data) {
    workOrdersResult.data.forEach(wo => { wo.customers = customerMap[wo.customer_id] || null; });
  }
  if (quotesResult.data) {
    quotesResult.data.forEach(q => { q.customers = customerMap[q.customer_id] || null; });
  }
  
  const activities = [];
  const { BanknotesIcon, DocumentTextIcon } = await import('@heroicons/react/24/outline');
  
  // Work order state changes
  if (workOrdersResult.data) {
    workOrdersResult.data.forEach(wo => {
      let message = '';
      if (wo.status === WORK_ORDER_STATUS.SCHEDULED) message = `Work order scheduled — ${wo.title}`;
      else if (wo.status === WORK_ORDER_STATUS.COMPLETED) message = `Work order completed — ${wo.title}`;
      if (message) {
        activities.push({
          id: `wo-${wo.id}`,
          workOrderId: wo.id,
          type: 'work_order',
          message,
          icon: DocumentTextIcon,
          ts: wo.updated_at,
          time: fmtDate(wo.updated_at)
        });
      }
    });
  }
  
  // Quote state changes
  if (quotesResult.data) {
    quotesResult.data.forEach(q => {
      let message = '';
      if (q.status === WORK_ORDER_STATUS.SENT) message = `Quote sent — ${q.title}`;
      else if (q.status === WORK_ORDER_STATUS.CHANGES_REQUESTED) message = `Changes requested — ${q.title}`;
      if (message) {
        activities.push({
          id: `quote-${q.id}`,
          workOrderId: q.id,
          type: 'quote',
          message,
          icon: DocumentTextIcon,
          ts: q.updated_at,
          time: fmtDate(q.updated_at)
        });
      }
    });
  }
  
  // Payment events
  if (paymentsResult.data && paymentsResult.data.length > 0) {
    paymentsResult.data.forEach(p => {
      let message = '';
      if (p.status === 'completed') message = `Payment received — $${Number(p.amount).toLocaleString()}`;
      else if (p.status === 'failed') message = `Payment failed — $${Number(p.amount).toLocaleString()}`;
      if (message) {
        activities.push({
          id: `payment-${p.id}`,
          type: 'payment',
          message,
          icon: BanknotesIcon,
          ts: p.updated_at,
          time: fmtDate(p.updated_at)
        });
      }
    });
  }

  // Invoice events — an invoice being sent to (or viewed by) the customer is a
  // meaningful business activity that the work-order/payment feeds never surface.
  if (invoicesResult.data && invoicesResult.data.length > 0) {
    invoicesResult.data.forEach(inv => {
      const num = inv.invoice_number || 'Invoice';
      const when = inv.sent_at || inv.updated_at;
      let message = '';
      if (inv.status === INVOICE_STATUS.VIEWED) {
        message = `Invoice viewed — ${num}`;
      } else {
        const amt = Number(inv.total_amount || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
        message = `Invoice sent — ${num} (${amt})`;
      }
      activities.push({
        id: `invoice-${inv.id}`,
        type: 'invoice',
        message,
        icon: DocumentTextIcon,
        ts: when,
        time: fmtDate(when)
      });
    });
  }

  activities.sort((a, b) => new Date(b.ts || b.time) - new Date(a.ts || a.time));
  return activities.slice(0, 10);
};

// ============== RISKS HOOK ==============
export const fetchRisks = async (companyId, utilizationPct = 0, capacityHours = 0) => {
  if (!companyId) return [];
  
  const supabase = getSupabaseClient();
  const newRisks = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayISO = today.toISOString();
  const sevenDaysAgo = new Date(today);
  sevenDaysAgo.setDate(today.getDate() - 7);
  const sevenDaysAgoISO = sevenDaysAgo.toISOString();
  
  // 1) Overdue Invoices
  //
  // A status in the A/R list and a due date in the past is NOT the definition of overdue —
  // it misses the question of whether anything is still owed. Live on Jerry's 2026-09-19:
  // ten invoices match that test and only NINE have a balance. INV-10006 was billed
  // $141.56, paid $141.56 and left sitting in `sent`, so the card said "10 invoices
  // overdue" and the page it opens showed 9. The Invoices hook already excludes it — its
  // own comment names this exact invoice — and utils/invoiceAr is the declared single
  // owner of the predicate, which this file was re-deriving inline.
  const { data: overdueInvoices, error: overdueInvError } = await supabase
    .from('invoices')
    .select('id,total_amount,amount_paid,balance_due,due_date,status')
    .eq('company_id', companyId)
    .in('status', AR_INVOICE_STATUSES)
    .lt('due_date', todayISO);

  const overdueReceivables = (overdueInvoices || []).filter(isReceivable);

  if (!overdueInvError && overdueReceivables.length > 0) {
    const totalAmount = overdueReceivables.reduce((sum, inv) => sum + invoiceBalance(inv), 0);
    newRisks.push({
      id: 'overdue-invoices',
      type: 'overdue_invoices',
      severity: 'high',
      title: 'Overdue Invoices',
      description: `${overdueReceivables.length} invoice${overdueReceivables.length > 1 ? 's' : ''} overdue — $${totalAmount.toLocaleString()} due`,
      count: overdueReceivables.length,
      action: { label: 'View Invoices', path: '/invoices?filter=overdue' }
    });
  }
  
  // 2) Aging Quotes
  const { data: agingQuotes, error: agingQuotesError } = await supabase
    .from('work_orders')
    .select('id,created_at')
    .eq('company_id', companyId)
    .eq('status', WORK_ORDER_STATUS.SENT)
    .lt('created_at', sevenDaysAgoISO);
  
  if (!agingQuotesError && agingQuotes && agingQuotes.length > 0) {
    newRisks.push({
      id: 'aging-quotes',
      type: 'aging_quotes',
      severity: 'medium',
      title: 'Aging Quotes',
      description: `${agingQuotes.length} quote${agingQuotes.length > 1 ? 's' : ''} waiting > 7 days — follow up needed`,
      count: agingQuotes.length,
      action: { label: 'View Quotes', path: '/quotes?filter=sent' }
    });
  }
  
  // 3) Unscheduled High-Value Jobs
  const { data: unscheduledHighValue, error: unscheduledHVError } = await supabase
    .from('work_orders')
    .select('id,total_amount')
    .eq('company_id', companyId)
    .in('status', [WORK_ORDER_STATUS.APPROVED, WORK_ORDER_STATUS.SCHEDULED])
    .is('scheduled_start', null)
    .gt('total_amount', 1000);
  
  if (!unscheduledHVError && unscheduledHighValue && unscheduledHighValue.length > 0) {
    newRisks.push({
      id: 'unscheduled-high-value',
      type: 'unscheduled_high_value',
      severity: 'high',
      title: 'Unscheduled High-Value Jobs',
      description: `${unscheduledHighValue.length} job${unscheduledHighValue.length > 1 ? 's' : ''} over $1K without a schedule`,
      count: unscheduledHighValue.length,
      action: { label: 'View Jobs', path: '/jobs?filter=unscheduled' }
    });
  }
  
  // 4) Jobs Starting Today with No Tech
  const todayStart = new Date(today); todayStart.setHours(0, 0, 0, 0);
  const todayEnd = new Date(today); todayEnd.setHours(23, 59, 59, 999);
  const { data: todayNoTech, error: todayNoTechError } = await supabase
    .from('work_orders')
    .select('id,scheduled_start')
    .eq('company_id', companyId)
    .gte('scheduled_start', todayStart.toISOString())
    .lte('scheduled_start', todayEnd.toISOString())
    .is('assigned_to', null);
  
  if (!todayNoTechError && todayNoTech && todayNoTech.length > 0) {
    newRisks.push({
      id: 'today-no-tech',
      type: 'today_no_tech',
      severity: 'high',
      title: 'Jobs Starting Today — No Tech Assigned',
      description: `${todayNoTech.length} job${todayNoTech.length > 1 ? 's' : ''} scheduled today without a technician`,
      count: todayNoTech.length,
      action: { label: 'Dispatch Now', path: '/dispatch' }
    });
  }
  
  // 5) Overdue Jobs
  const nowISO = new Date().toISOString();
  const { data: overdueJobs, error: overdueJobsError } = await supabase
    .from('work_orders')
    .select('id,scheduled_end')
    .eq('company_id', companyId)
    .eq('status', WORK_ORDER_STATUS.IN_PROGRESS)
    .lt('scheduled_end', nowISO);
  
  if (!overdueJobsError && overdueJobs && overdueJobs.length > 0) {
    newRisks.push({
      id: 'overdue-jobs',
      type: 'overdue_jobs',
      severity: 'medium',
      title: 'Overdue Jobs',
      description: `${overdueJobs.length} job${overdueJobs.length > 1 ? 's' : ''} past scheduled end date still in progress`,
      count: overdueJobs.length,
      action: { label: 'View Jobs', path: '/jobs?filter=overdue' }
    });
  }
  
  // 6) Low Utilization Warning
  // #6247: the guard is "there is capacity to measure", not "utilization is above 0" -- a
  // rounded 0% never warned while 1% did.
  if (capacityHours > 0 && utilizationPct < 50) {
    newRisks.push({
      id: 'low-utilization',
      type: 'low_utilization',
      severity: 'low',
      title: 'Low Utilization Warning',
      description: `Team utilization at ${utilizationPct}% — consider scheduling more jobs or reducing capacity`,
      count: utilizationPct,
      action: { label: 'View Schedule', path: '/jobs' }
    });
  }
  
  return newRisks;
};
